import {
  List,
  ListItem,
  ListItemText,
  Radio,
  Paper,
  Box,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { useTranslation } from 'react-i18next';

interface AllowedEventsListProps {
  availableEvents: string[];
  selectedEvent: string | null;
  isLoading: boolean;
  onSelectionChange: (event: string | null) => void;
}

export const AllowedEventsList = ({
  availableEvents,
  selectedEvent,
  isLoading,
  onSelectionChange
}: AllowedEventsListProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const handleSelect = (event: string) => {
    onSelectionChange(event === selectedEvent ? null : event);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200
        }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Paper
      elevation={1}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {t('settings:allowedEvents.list.instructions')}
        </Typography>
      </Box>

      <List sx={{ py: 0 }}>
        {availableEvents.map((event, index) => {
          const isSelected = event === selectedEvent;
          const isLastItem = index === availableEvents.length - 1;

          return (
            <ListItem
              key={event}
              onClick={() => handleSelect(event)}
              sx={{
                cursor: 'pointer',
                py: 1.5,
                px: 2,
                borderBottom: !isLastItem
                  ? `1px solid ${theme.palette.divider}`
                  : 'none',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                },
                ...(isSelected && {
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  }
                })
              }}
            >
              <Radio checked={isSelected} color="primary" sx={{ mr: 1 }} />
              <ListItemText
                primary={event}
                primaryTypographyProps={{
                  fontWeight: isSelected ? 500 : 400,
                  color: isSelected ? 'primary.main' : 'text.primary'
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};
